import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import {
  Users, Euro, TrendingUp, Award, CheckCircle2, ArrowRight,
  Building2, Percent, HeadphonesIcon, GraduationCap, Star,
  Zap, Shield, Clock, Gift, ChevronRight, Mail, Phone, Loader2
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PartenaireComptablePage = () => {
  const [formData, setFormData] = useState({
    cabinet_name: '',
    contact_name: '',
    email: '',
    phone: '',
    num_clients: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await fetch(`${API_URL}/api/partners/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      
      if (response.ok) {
        setSubmitted(true);
        toast.success('Demande envoyée ! Nous vous contacterons sous 24h.');
      } else {
        toast.error('Erreur lors de l\'envoi. Veuillez réessayer.');
      }
    } catch (error) {
      toast.error('Erreur de connexion. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  };

  const tiers = [
    {
      name: 'Bronze',
      commission: '15%',
      minClients: '1+',
      color: 'from-amber-600 to-amber-700',
      features: [
        'Commission 15% récurrente',
        'Dashboard partenaire',
        'Support email prioritaire',
        'Matériel marketing'
      ]
    },
    {
      name: 'Silver',
      commission: '20%',
      minClients: '10+',
      color: 'from-slate-400 to-slate-500',
      popular: true,
      features: [
        'Commission 20% récurrente',
        'Formation produit offerte',
        'Support téléphonique',
        'Co-branding possible',
        'Webinaires exclusifs'
      ]
    },
    {
      name: 'Gold',
      commission: '25%',
      minClients: '50+',
      color: 'from-yellow-500 to-amber-500',
      features: [
        'Commission 25% récurrente',
        'Account manager dédié',
        'Tarifs préférentiels clients',
        'Événements VIP',
        'Référencement prioritaire',
        'API personnalisée'
      ]
    }
  ];

  const benefits = [
    {
      icon: Euro,
      title: 'Revenus récurrents',
      description: 'Gagnez jusqu\'à 25% de commission chaque mois, tant que vos clients restent abonnés.'
    },
    {
      icon: Users,
      title: 'Fidélisez vos clients',
      description: 'Proposez une solution complète e-facturation 2026 et renforcez votre relation client.'
    },
    {
      icon: Shield,
      title: 'Conformité garantie',
      description: 'Vos clients sont automatiquement conformes aux nouvelles obligations légales.'
    },
    {
      icon: HeadphonesIcon,
      title: 'Support dédié',
      description: 'Une équipe dédiée pour vous accompagner et former vos collaborateurs.'
    },
    {
      icon: Zap,
      title: 'Gain de temps',
      description: 'Moins de questions de vos clients, plus de temps pour le conseil à valeur ajoutée.'
    },
    {
      icon: TrendingUp,
      title: 'Tableau de bord',
      description: 'Suivez vos commissions, vos clients et vos performances en temps réel.'
    }
  ];

  const stats = [
    { value: 'TPE/PME', label: 'Clients cibles' },
    { value: '25%', label: 'Commission max' },
    { value: '7j', label: 'Essai gratuit' },
    { value: '24h', label: 'Réponse garantie' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Navigation */}
      <nav className="bg-white/80 backdrop-blur-sm border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-600 to-cyan-500 flex items-center justify-center">
              <Zap className="h-6 w-6 text-white" />
            </div>
            <span className="text-xl font-bold text-slate-900">SmartFacture AI</span>
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/login">
              <Button variant="ghost">Connexion</Button>
            </Link>
            <Link to="/register">
              <Button className="bg-blue-600 hover:bg-blue-700">Essai gratuit</Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-800" />
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-20 w-72 h-72 bg-white rounded-full blur-3xl" />
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-cyan-300 rounded-full blur-3xl" />
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 py-20 md:py-28">
          <div className="text-center">
            <Badge className="bg-white/20 text-white border-white/30 mb-6">
              <Award className="h-4 w-4 mr-2" />
              Programme Partenaire Officiel
            </Badge>
            
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
              Devenez Partenaire
              <span className="block text-cyan-300">SmartFacture AI</span>
            </h1>
            
            <p className="text-xl text-blue-100 max-w-3xl mx-auto mb-8">
              Accompagnez vos clients TPE/PME vers la conformité e-facturation 2026 
              et générez des revenus récurrents jusqu'à <strong className="text-white">25% de commission</strong>.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg" 
                className="bg-white text-blue-700 hover:bg-blue-50 text-lg px-8"
                onClick={() => document.getElementById('inscription').scrollIntoView({ behavior: 'smooth' })}
              >
                Devenir partenaire
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button 
                size="lg" 
                variant="outline" 
                className="border-white/30 text-white hover:bg-white/10 text-lg px-8"
              >
                Télécharger la brochure
              </Button>
            </div>
          </div>
          
          {/* Stats */}
          <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-6">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-3xl md:text-4xl font-bold text-white">{stat.value}</div>
                <div className="text-blue-200">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <Badge className="bg-blue-100 text-blue-700 mb-4">Avantages</Badge>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Pourquoi devenir partenaire ?
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Un programme pensé pour les experts-comptables qui veulent accompagner 
              leurs clients vers l'e-facturation tout en développant leurs revenus.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => (
              <Card key={index} className="border-slate-200 hover:border-blue-200 hover:shadow-lg transition-all">
                <CardContent className="p-6">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-100 to-cyan-100 flex items-center justify-center mb-4">
                    <benefit.icon className="h-6 w-6 text-blue-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">{benefit.title}</h3>
                  <p className="text-slate-600">{benefit.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Commission Tiers */}
      <section className="py-20 px-4 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <Badge className="bg-emerald-100 text-emerald-700 mb-4">Commissions</Badge>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Choisissez votre niveau
            </h2>
            <p className="text-lg text-slate-600">
              Plus vous référez de clients, plus votre commission augmente.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {tiers.map((tier, index) => (
              <Card 
                key={index} 
                className={`relative overflow-hidden ${tier.popular ? 'border-2 border-blue-500 shadow-xl' : 'border-slate-200'}`}
              >
                {tier.popular && (
                  <div className="absolute top-0 right-0 bg-blue-500 text-white text-xs font-bold px-3 py-1 rounded-bl-lg">
                    POPULAIRE
                  </div>
                )}
                <CardHeader className={`bg-gradient-to-r ${tier.color} text-white`}>
                  <div className="text-center py-4">
                    <CardTitle className="text-2xl mb-2">{tier.name}</CardTitle>
                    <div className="text-5xl font-bold">{tier.commission}</div>
                    <div className="text-white/80">commission récurrente</div>
                    <div className="mt-2 text-sm bg-white/20 rounded-full px-3 py-1 inline-block">
                      {tier.minClients} clients référés
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="p-6">
                  <ul className="space-y-3">
                    {tier.features.map((feature, fIndex) => (
                      <li key={fIndex} className="flex items-start gap-3">
                        <CheckCircle2 className="h-5 w-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                        <span className="text-slate-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
          
          {/* Commission Calculator */}
          <Card className="mt-12 border-blue-200 bg-gradient-to-r from-blue-50 to-cyan-50">
            <CardContent className="p-8">
              <div className="flex flex-col md:flex-row items-center justify-between gap-6">
                <div>
                  <h3 className="text-xl font-bold text-slate-900 mb-2">
                    💰 Exemple de revenus
                  </h3>
                  <p className="text-slate-600">
                    Avec <strong>20 clients</strong> au tarif Pro (29€/mois) et <strong>20% de commission</strong> :
                  </p>
                </div>
                <div className="text-center">
                  <div className="text-4xl font-bold text-blue-600">156€</div>
                  <div className="text-slate-500">par mois, récurrent</div>
                  <div className="text-sm text-emerald-600 font-medium mt-1">= 1 872€/an</div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* How it works */}
      <section className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <Badge className="bg-purple-100 text-purple-700 mb-4">Processus</Badge>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Comment ça marche ?
            </h2>
          </div>
          
          <div className="grid md:grid-cols-4 gap-8">
            {[
              { step: '1', title: 'Inscription', description: 'Remplissez le formulaire et nous vous contactons sous 24h.' },
              { step: '2', title: 'Formation', description: 'Accédez à notre formation produit et obtenez votre certification.' },
              { step: '3', title: 'Référez', description: 'Proposez SmartFacture à vos clients avec votre lien partenaire.' },
              { step: '4', title: 'Gagnez', description: 'Recevez vos commissions chaque mois, automatiquement.' }
            ].map((item, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-600 to-cyan-500 text-white text-2xl font-bold flex items-center justify-center mx-auto mb-4">
                  {item.step}
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2">{item.title}</h3>
                <p className="text-slate-600">{item.description}</p>
                {index < 3 && (
                  <ChevronRight className="h-6 w-6 text-slate-300 mx-auto mt-4 hidden md:block rotate-0 md:rotate-0" />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Registration Form */}
      <section id="inscription" className="py-20 px-4 bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <Badge className="bg-white/20 text-white border-white/30 mb-4">
              <Gift className="h-4 w-4 mr-2" />
              Inscription gratuite
            </Badge>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Rejoignez le programme
            </h2>
            <p className="text-blue-200">
              Remplissez ce formulaire et nous vous contacterons sous 24h.
            </p>
          </div>
          
          {submitted ? (
            <Card className="bg-white/10 backdrop-blur border-white/20">
              <CardContent className="p-12 text-center">
                <div className="w-20 h-20 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto mb-6">
                  <CheckCircle2 className="h-10 w-10 text-emerald-400" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-4">Demande envoyée !</h3>
                <p className="text-blue-200 mb-6">
                  Merci pour votre intérêt. Notre équipe partenariats vous contactera sous 24h 
                  pour finaliser votre inscription.
                </p>
                <Link to="/">
                  <Button className="bg-white text-blue-700 hover:bg-blue-50">
                    Retour à l'accueil
                  </Button>
                </Link>
              </CardContent>
            </Card>
          ) : (
            <Card className="bg-white/10 backdrop-blur border-white/20">
              <CardContent className="p-8">
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <Label className="text-white">Nom du cabinet *</Label>
                      <Input
                        value={formData.cabinet_name}
                        onChange={(e) => setFormData({...formData, cabinet_name: e.target.value})}
                        placeholder="Cabinet Dupont & Associés"
                        className="bg-white/10 border-white/20 text-white placeholder:text-slate-400"
                        required
                      />
                    </div>
                    <div>
                      <Label className="text-white">Nom du contact *</Label>
                      <Input
                        value={formData.contact_name}
                        onChange={(e) => setFormData({...formData, contact_name: e.target.value})}
                        placeholder="Jean Dupont"
                        className="bg-white/10 border-white/20 text-white placeholder:text-slate-400"
                        required
                      />
                    </div>
                  </div>
                  
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <Label className="text-white">Email professionnel *</Label>
                      <Input
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({...formData, email: e.target.value})}
                        placeholder="contact@cabinet.fr"
                        className="bg-white/10 border-white/20 text-white placeholder:text-slate-400"
                        required
                      />
                    </div>
                    <div>
                      <Label className="text-white">Téléphone</Label>
                      <Input
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => setFormData({...formData, phone: e.target.value})}
                        placeholder="01 23 45 67 89"
                        className="bg-white/10 border-white/20 text-white placeholder:text-slate-400"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <Label className="text-white">Nombre de clients TPE/PME</Label>
                    <Input
                      value={formData.num_clients}
                      onChange={(e) => setFormData({...formData, num_clients: e.target.value})}
                      placeholder="Ex: 50-100"
                      className="bg-white/10 border-white/20 text-white placeholder:text-slate-400"
                    />
                  </div>
                  
                  <div>
                    <Label className="text-white">Message (optionnel)</Label>
                    <Textarea
                      value={formData.message}
                      onChange={(e) => setFormData({...formData, message: e.target.value})}
                      placeholder="Parlez-nous de votre cabinet et vos attentes..."
                      className="bg-white/10 border-white/20 text-white placeholder:text-slate-400 min-h-[100px]"
                    />
                  </div>
                  
                  <Button 
                    type="submit" 
                    className="w-full bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-lg py-6"
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                        Envoi en cours...
                      </>
                    ) : (
                      <>
                        Devenir partenaire
                        <ArrowRight className="ml-2 h-5 w-5" />
                      </>
                    )}
                  </Button>
                  
                  <p className="text-center text-blue-300 text-sm">
                    En soumettant ce formulaire, vous acceptez d'être contacté par notre équipe.
                  </p>
                </form>
              </CardContent>
            </Card>
          )}
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 px-4">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">Questions fréquentes</h2>
          </div>
          
          <div className="space-y-4">
            {[
              { q: "Comment sont calculées les commissions ?", a: "Vous recevez un pourcentage de l'abonnement mensuel de chaque client référé, tant qu'il reste abonné. Les commissions sont versées le 15 de chaque mois." },
              { q: "Puis-je utiliser SmartFacture pour mon propre cabinet ?", a: "Absolument ! En tant que partenaire, vous bénéficiez d'un accès gratuit à SmartFacture pour votre cabinet." },
              { q: "Y a-t-il un engagement minimum ?", a: "Non, le programme est sans engagement. Vous pouvez le quitter à tout moment tout en conservant vos commissions acquises." },
              { q: "Comment mes clients s'inscrivent-ils ?", a: "Vous recevez un lien de parrainage unique. Vos clients s'inscrivent via ce lien et sont automatiquement rattachés à votre compte partenaire." }
            ].map((faq, index) => (
              <Card key={index} className="border-slate-200">
                <CardContent className="p-6">
                  <h3 className="font-semibold text-slate-900 mb-2">{faq.q}</h3>
                  <p className="text-slate-600">{faq.a}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Footer CTA */}
      <section className="py-12 px-4 bg-slate-100">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-slate-600 mb-4">
            Des questions ? Contactez notre équipe partenariats
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="mailto:partenaires@smartfacture-ai.com" className="flex items-center justify-center gap-2 text-blue-600 hover:text-blue-700">
              <Mail className="h-5 w-5" />
              partenaires@smartfacture-ai.com
            </a>
            <span className="hidden sm:block text-slate-300">|</span>
            <a href="tel:+33123456789" className="flex items-center justify-center gap-2 text-blue-600 hover:text-blue-700">
              <Phone className="h-5 w-5" />
              01 23 45 67 89
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PartenaireComptablePage;
